import { defineStore } from "pinia";
import localStorage from "../Common/store";
import competitorResults, { CalculatedAcademyResultsResponse, ParentResultsMap, SelectedRule } from "../Api/competitorResultsApi";

export const TAB_SHARE_RESULTS = "shareResults";
export const TAB_PUBLISHED_RESULTS = "publishedResults";

function getLocalStorageKey(eventId: number): string {
    return "academyResultsOptions_v3_" + eventId;
}

export function storeOptionsInLocalStorage(eventId: number, options: object): void {
    const json = JSON.stringify(options);
    localStorage.set(getLocalStorageKey(eventId), json);
}

export type AcademyResultsStoreType = ReturnType<typeof useAcademyResultsStore>;

export function getOptionsFromState(store: AcademyResultsStoreType): object {
    return {
        mergeAffiliations: store.mergeAffiliations,
        showExAequoPlaces: store.showExAequoPlaces,
        selectedRules: store.selectedRules,
    };
}

export const useAcademyResultsStore = defineStore("main", {
    state: () => ({
        view: TAB_SHARE_RESULTS as typeof TAB_SHARE_RESULTS | typeof TAB_PUBLISHED_RESULTS,
        eventId: 0,

        mergeAffiliations: true,
        showExAequoPlaces: false,

        academyResults: [] as CalculatedAcademyResultsResponse["mainResults"],
        parentResultsMap: {} as ParentResultsMap,
        expandedResults: {} as Record<number, boolean>,
        publishingMode: false,

        selectedRules: [] as SelectedRule[],
    }),

    getters: {
        isCategorySelectedInSelectedRules(state) {
            return (parameterId: number): boolean => {
                for (const rule of state.selectedRules) {
                    if (rule.input.selectedCategories.includes(parameterId)) {
                        return true;
                    }
                }
                return false;
            };
        },
    },
    actions: {
        addSelectedRule(rule: SelectedRule) {
            this.selectedRules.push(rule);
            storeOptionsInLocalStorage(this.eventId, getOptionsFromState(this));
        },
        removeRule(index: number) {
            this.selectedRules.splice(index, 1);

            this.expandedResults = {};
            this.academyResults = [];
            this.parentResultsMap = {};
            storeOptionsInLocalStorage(this.eventId, getOptionsFromState(this));

            if (this.selectedRules.length === 0) {
                return;
            }

            return this.fetchAcademyResults();
        },

        toggleMergedResults(academyId: number) {
            this.expandedResults[academyId] = !this.expandedResults[academyId];
        },

        loadFromLocalStorage() {
            const options = localStorage.get(getLocalStorageKey(this.eventId));
            if (!options) {
                return;
            }

            const parsed = JSON.parse(options);
            this.mergeAffiliations = parsed.mergeAffiliations ?? true;
            this.showExAequoPlaces = parsed.showExAequoPlaces ?? false;
            this.selectedRules = parsed.selectedRules ?? [];
        },

        fetchAcademyResults(): Promise<void> {
            if (!this.selectedRules.length) {
                return Promise.resolve();
            }

            return competitorResults
                .calculateAcademyResults(this.eventId, {
                    showExAequoPlaces: this.showExAequoPlaces,
                    mergeAffiliations: this.mergeAffiliations,
                    selectedRules: this.selectedRules,
                })
                .then(academyResults => {
                    this.expandedResults = {};
                    this.academyResults = academyResults.mainResults;
                    this.parentResultsMap = academyResults.parentResultsMap;
                });
        },
    },
});
